import { useParams, useLocation } from "wouter";
import { useProposal } from "@/hooks/useProposals";
import { useTrip } from "@/hooks/useTrip";
import { useTripComponents } from "@/hooks/useTripComponents";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { format } from "date-fns";
import { ArrowLeft, Check, Clock, DollarSign, X, Calendar, MapPin } from "lucide-react";
import { useMemo, useState } from "react";
import { Loader } from "lucide-react";

export function ProposalView() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const proposalId = id ? parseInt(id) : null;
  const [status, setStatus] = useState<string>("");

  // Get proposal data
  const {
    data: proposal,
    isLoading: isProposalLoading,
    error: proposalError,
  } = useProposal(proposalId);

  // Get trip data
  const tripId = proposal?.tripId ?? 0;

  const {
    trip,
    isLoading: isTripLoading,
  } = useTrip(tripId);

  // Get trip components
  const {
    components,
    isLoading: isComponentsLoading,
    error: componentsError,
  } = useTripComponents(tripId, !!proposal && !!tripId);

  const tripCountries = useMemo(() => {
    if (!trip) {
      return [] as string[];
    }

    if (Array.isArray(trip.countries)) {
      return trip.countries.filter((country): country is string => typeof country === 'string');
    }

    if (typeof trip.countries === 'string' && trip.countries.trim()) {
      return [trip.countries];
    }

    return [] as string[];
  }, [trip]);

  const highlights = useMemo(() => {
    if (!proposal) {
      return [] as string[];
    }

    const raw = proposal.highlights;
    if (Array.isArray(raw)) {
      return raw
        .map((item) => (typeof item === 'string' ? item : String(item)))
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    return [] as string[];
  }, [proposal]);

  const pricingInfo = useMemo(() => {
    if (!proposal || !proposal.pricing || typeof proposal.pricing !== 'object' || Array.isArray(proposal.pricing)) {
      return null;
    }

    const record = proposal.pricing as Record<string, unknown>;
    const parseAmount = (value: unknown): number | null => {
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };

    const totalPrice = parseAmount(record.totalPrice ?? record.total ?? record.amount);
    const perPerson = Boolean(record.perPerson);

    return { totalPrice, perPerson };
  }, [proposal]);

  // Format dates with timezone consideration
  const formatDate = (date: string | Date | null) => {
    if (!date) return "";
    
    // Create new date object and handle timezone consistently
    const dateObj = new Date(date);
    
    // Using UTC date components to avoid timezone shifts
    const year = dateObj.getUTCFullYear();
    const month = dateObj.getUTCMonth();
    const day = dateObj.getUTCDate();
    
    // Create a new date object with the correct components
    const correctedDate = new Date(year, month, day);
    
    // Format the date for display
    return format(correctedDate, "MMM d, yyyy");
  };

  // Group components by day
  const groupedComponents = components
    ? components.reduce<{ [key: number]: any[] }>((acc, component) => {
        const day = component.dayIndex || 0;
        if (!acc[day]) {
          acc[day] = [];
        }
        acc[day].push(component);
        return acc;
      }, {})
    : {};
    
  // Get sorted day indexes to ensure they display in the correct order
  const sortedDayIndexes = Object.keys(groupedComponents)
    .map(Number)
    .sort((a, b) => a - b);

  // Handle approve/reject actions
  const handleUpdateStatus = (newStatus: string) => {
    setStatus(newStatus);
    // In a real implementation, this would call an API to update the proposal status
    alert(`Proposal ${newStatus}. In a real implementation, this would update the status in the database.`);
  };

  // Get component icon based on type
  const getComponentIcon = (type: string) => {
    switch (type) {
      case "flight":
        return "✈️";
      case "hotel":
        return "🏨";
      case "activity":
        return "🏄‍♂️";
      case "food":
        return "🍽️";
      case "transport":
        return "🚕";
      default:
        return "📌";
    }
  };

  // Loading state
  const isLoading = isProposalLoading || isTripLoading || isComponentsLoading;
  const error = proposalError || componentsError;

  if (isLoading) {
    return (
      <div className="container max-w-5xl mx-auto py-8 px-4 min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader className="animate-spin h-12 w-12 text-primary mb-4" />
          <p className="text-muted-foreground">Loading proposal data...</p>
        </div>
      </div>
    );
  }

  if (error || !proposal || !trip) {
    return (
      <div className="container max-w-5xl mx-auto py-8 px-4">
        <div className="bg-destructive/10 text-destructive p-6 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">Error Loading Proposal</h2>
          <p>We encountered an error while loading the proposal. Please try again later.</p>
          <Button className="mt-4" variant="outline" onClick={() => navigate("/trips")}>
            Return to Trips
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen">
      {/* Navigation */}
      <div className="bg-muted py-4 border-b">
        <div className="container max-w-5xl mx-auto px-4">
          <Button variant="outline" size="sm" onClick={() => navigate(`/trips/${trip.id}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Trip
          </Button>
        </div>
      </div>

      {/* Cover section */}
      <div 
        className="bg-cover bg-center h-64 flex items-center justify-center relative"
        style={{
          backgroundImage: proposal.coverImageUrl
            ? `url(${proposal.coverImageUrl})`
            : "linear-gradient(to right, #4f46e5, #6366f1)"
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{proposal.title}</h1>
          <div className="flex justify-center items-center gap-3 mb-2">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              <span>{formatDate(trip.startDate)} - {formatDate(trip.endDate)}</span>
            </div>
            {tripCountries.length > 0 && (
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{tripCountries.join(", ")}</span>
              </div>
            )}
          </div>
          <Badge 
            className="text-sm py-1 px-3"
            variant={
              proposal.status === "approved" ? "default" :
              proposal.status === "rejected" ? "destructive" :
              proposal.status === "sent" ? "secondary" : "outline"
            }
          >
            {proposal.status.charAt(0).toUpperCase() + proposal.status.slice(1)}
          </Badge>
        </div>
      </div>

      {/* Main content */}
      <div className="container max-w-5xl mx-auto py-8 px-4">
        {/* Approval actions for "sent" proposals */}
        {proposal.status === "sent" && (
          <div className="bg-muted p-4 rounded-lg mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <div className="mb-4 sm:mb-0">
                <h3 className="font-bold text-lg">Proposal Feedback</h3>
                <p className="text-muted-foreground">Please review this proposal and provide your feedback</p>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => handleUpdateStatus("rejected")}>
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                <Button onClick={() => handleUpdateStatus("approved")}>
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Trip highlights */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Trip Highlights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {highlights.map((highlight, index) => (
              <div key={index} className="flex items-start">
                <div className="bg-primary/10 text-primary p-2 rounded-full mr-3">
                  <Check className="h-4 w-4" />
                </div>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Advisor note */}
        {proposal.advisorNote && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Advisor Note</h2>
            <Card>
              <CardContent className="p-6">
                <p className="text-muted-foreground leading-relaxed">
                  {proposal.advisorNote}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Itinerary */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4">Your Itinerary</h2>
          
          {Object.keys(groupedComponents).length > 0 ? (
            <div className="space-y-6">
              {sortedDayIndexes.map((rawDayIndex, index) => {
                // Use the index in the sorted array to determine actual day number
                const displayDayIndex = index;
                const dayComponents = groupedComponents[rawDayIndex];
                
                const hasTripStart = !!trip.startDate;
                let dayLabel = `Day ${displayDayIndex + 1}`;
                if (hasTripStart) {
                  const startDate = new Date(trip.startDate!);
                  const year = startDate.getUTCFullYear();
                  const month = startDate.getUTCMonth();
                  const day = startDate.getUTCDate();
                  const dayDate = new Date(year, month, day + displayDayIndex);
                  dayLabel += ` - ${formatDate(dayDate)}`;
                }
                
                return (
                  <div key={index} className="border rounded-lg overflow-hidden">
                    <div className="bg-muted px-4 py-3 border-b">
                      <h3 className="font-semibold">
                        {dayLabel}
                      </h3>
                    </div>
                    <div className="divide-y">
                      {dayComponents.map((component, compIndex) => (
                        <div key={compIndex} className="p-4 flex items-start">
                          <div className="mr-4 text-2xl">
                            {getComponentIcon(component.type)}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between mb-1">
                              <h4 className="font-medium">{component.title}</h4>
                              {component.startTime && (
                                <span className="text-sm text-muted-foreground flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {component.startTime}
                                  {component.endTime && ` - ${component.endTime}`}
                                </span>
                              )}
                            </div>
                            {component.description && (
                              <p className="text-sm text-muted-foreground">
                                {component.description}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 border border-dashed rounded-lg">
              <p className="text-muted-foreground">No itinerary components available</p>
            </div>
          )}
        </div>

        {/* Terms and conditions */}
        {proposal.termsAndConditions && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Terms & Conditions</h2>
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none text-muted-foreground">
                  <p>{proposal.termsAndConditions}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pricing section */}
        {pricingInfo && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Pricing</h2>
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold flex items-center">
                      <DollarSign className="h-5 w-5 mr-1 text-primary" />
                      Total Price
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Based on your selected accommodations and activities
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold">
                      {pricingInfo.totalPrice != null ? `$${pricingInfo.totalPrice.toLocaleString()}` : 'TBD'}
                    </span>
                    {pricingInfo.perPerson && (
                      <p className="text-sm text-muted-foreground">Per person</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Action footer */}
        <div className="flex justify-center mt-12">
          <div className="space-x-4">
            <Button variant="outline" onClick={() => navigate(`/trips/${trip.id}`)}>
              Back to Trip
            </Button>
            {proposal.status === "sent" && (
              <>
                <Button variant="outline" onClick={() => handleUpdateStatus("rejected")}>
                  <X className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                <Button onClick={() => handleUpdateStatus("approved")}>
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
